import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Loader2,
  RefreshCw,
  AlertCircle,
  Search,
  FileSpreadsheet,
  Database,
  Folder,
  LayoutDashboard,
  Filter as FilterIcon,
  Settings2,
  Sparkles,
  Library,
  Copy,
  Play,
} from 'lucide-react';
import { getApiBase } from '../apiBase';
import RunReportModal from './RunReportModal';

const API = axios.create({
  baseURL: getApiBase(),
  withCredentials: true,
  timeout: 25000,
});

const STATUS_POLL_MS = 4000;

function formatStatusError(status, message, { inProgress = false, hasIndex = false } = {}) {
  if (status === 504 || /timeout/i.test(message || '')) {
    if (inProgress) {
      return (
        'Status poll timed out while the scan is still running. ' +
        'The catalog index continues in the background — counts may lag until the scan finishes. ' +
        'Refresh the page in a minute if this persists.'
      );
    }
    if (hasIndex) {
      return (
        'Status request timed out (Heroku 30s gateway limit). ' +
        'Your indexed catalog is still usable — try Quick scan or refresh the page.'
      );
    }
    return 'Request timed out. Try Quick scan (1–3 min) instead of Full scan on Heroku.';
  }
  return message || (status ? `HTTP ${status}` : 'Failed to load status');
}

async function getWithRetry(url, config = {}, retries = 2) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await API.get(url, config);
    } catch (err) {
      lastErr = err;
      const status = err.response?.status;
      const retryable = status === 504 || status === 503 || /timeout/i.test(err.message || '');
      if (!retryable || attempt === retries) throw err;
      await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
    }
  }
  throw lastErr;
}

// Single source of truth for how every catalog `kind` is rendered.
// Order in this map also drives the order of filter chips on the page.
const KIND_META = {
  bipReport: {
    label: 'BIP Report',
    short: 'Report',
    icon: FileSpreadsheet,
    chipClass: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    iconClass: 'text-emerald-600',
  },
  bipDataModel: {
    label: 'BIP Data Model',
    short: 'Data Model',
    icon: Database,
    chipClass: 'bg-violet-100 text-violet-700 border-violet-200',
    iconClass: 'text-violet-600',
  },
  otbiSubjectArea: {
    label: 'OTBI Subject Area',
    short: 'Subject Area',
    icon: Library,
    chipClass: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    iconClass: 'text-cyan-600',
  },
  otbiAnalysis: {
    label: 'OTBI Analysis',
    short: 'Analysis',
    icon: Sparkles,
    chipClass: 'bg-amber-100 text-amber-700 border-amber-200',
    iconClass: 'text-amber-600',
  },
  otbiDashboard: {
    label: 'OTBI Dashboard',
    short: 'Dashboard',
    icon: LayoutDashboard,
    chipClass: 'bg-blue-100 text-blue-700 border-blue-200',
    iconClass: 'text-blue-600',
  },
  otbiFilter: {
    label: 'OTBI Filter',
    short: 'Filter',
    icon: FilterIcon,
    chipClass: 'bg-slate-100 text-slate-700 border-slate-200',
    iconClass: 'text-slate-600',
  },
  otbiPrompt: {
    label: 'OTBI Prompt',
    short: 'Prompt',
    icon: Settings2,
    chipClass: 'bg-pink-100 text-pink-700 border-pink-200',
    iconClass: 'text-pink-600',
  },
  bipFolder: {
    label: 'BIP Folder',
    short: 'Folder',
    icon: Folder,
    chipClass: 'bg-slate-100 text-slate-600 border-slate-200',
    iconClass: 'text-slate-400',
  },
  otbiFolder: {
    label: 'OTBI Folder',
    short: 'Folder',
    icon: Folder,
    chipClass: 'bg-slate-100 text-slate-600 border-slate-200',
    iconClass: 'text-slate-400',
  },
};

const SEARCHABLE_KINDS = [
  'bipReport',
  'bipDataModel',
  'otbiSubjectArea',
  'otbiAnalysis',
  'otbiDashboard',
];

function fmtCount(n) {
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString();
}

function relTime(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return d.toLocaleString();
  } catch (_) {
    return iso;
  }
}

function statusPill(run) {
  if (!run) return null;
  const map = {
    running: 'bg-cyan-100 text-cyan-700',
    queued: 'bg-slate-100 text-slate-600',
    succeeded: 'bg-emerald-100 text-emerald-700',
    partial: 'bg-amber-100 text-amber-700',
    failed: 'bg-rose-100 text-rose-700',
    cancelled: 'bg-slate-100 text-slate-500',
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${map[run.status] || 'bg-slate-100'}`}>
      {run.status}
    </span>
  );
}

function CountTile({ icon: Icon, label, count, accent }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3">
      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${accent}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
        <div className="text-lg font-semibold text-slate-800">{fmtCount(count)}</div>
      </div>
    </div>
  );
}

export default function ReportsCatalog({ onAskCopilot }) {
  const [status, setStatus] = useState(null);
  const [statusErr, setStatusErr] = useState('');
  const [statusWarn, setStatusWarn] = useState('');
  const [statusLoading, setStatusLoading] = useState(true);

  const [query, setQuery] = useState('');
  const [activeKinds, setActiveKinds] = useState(() => new Set(SEARCHABLE_KINDS));
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsError, setRowsError] = useState('');
  const [strategy, setStrategy] = useState('text');
  const debounceRef = useRef(null);

  const [scanning, setScanning] = useState(false);
  const [scope, setScope] = useState('quick');

  // Active "Run this report" row (null when modal is closed).
  const [runRow, setRunRow] = useState(null);

  // ─── data fetchers ──
  async function loadStatus({ softFail = false } = {}) {
    try {
      const res = await getWithRetry('/api/reports/status', { validateStatus: () => true });
      if (res.status === 200) {
        setStatus((prev) => {
          const next = res.data;
          if (next.countsStale && prev?.catalogTotals && prev.grandTotal > 0) {
            return {
              ...next,
              catalogTotals: prev.catalogTotals,
              grandTotal: prev.grandTotal,
            };
          }
          return next;
        });
        setStatusErr('');
        setStatusWarn(
          res.data.countsStale && (res.data.inFlight || res.data.latestRun?.status === 'running')
            ? 'Counts may lag while the scan is running.'
            : ''
        );
      } else {
        const msg = formatStatusError(res.status, res.data?.error, {
          inProgress: status?.latestRun?.status === 'running' || status?.latestRun?.status === 'queued',
          hasIndex: (status?.grandTotal || 0) > 0,
        });
        if (softFail && (status?.grandTotal || 0) > 0) setStatusWarn(msg);
        else setStatusErr(msg);
      }
    } catch (err) {
      const statusCode = err.response?.status;
      const msg = formatStatusError(statusCode, err.message, {
        inProgress: status?.latestRun?.status === 'running' || status?.latestRun?.status === 'queued',
        hasIndex: (status?.grandTotal || 0) > 0,
      });
      if (softFail && (status?.grandTotal || 0) > 0) setStatusWarn(msg);
      else setStatusErr(msg);
    } finally {
      setStatusLoading(false);
    }
  }

  async function loadRows(q, kinds) {
    setRowsLoading(true);
    setRowsError('');
    try {
      const trimmed = String(q || '').trim();
      const kindParam = kinds && kinds.size && kinds.size !== Object.keys(KIND_META).length
        ? [...kinds].join(',')
        : undefined;
      let res;
      if (trimmed) {
        res = await API.get('/api/reports/search', {
          params: { q: trimmed, ...(kindParam ? { kind: kindParam } : {}) },
          validateStatus: () => true,
        });
        if (res.status === 200) {
          setRows(res.data.rows || []);
          setStrategy(res.data.strategy || 'text');
        } else {
          setRowsError(res.data?.error || `HTTP ${res.status}`);
        }
      } else {
        res = await API.get('/api/reports/browse', {
          params: { limit: 100, ...(kindParam ? { kind: kindParam } : {}) },
          validateStatus: () => true,
        });
        if (res.status === 200) {
          setRows(res.data.rows || []);
          setStrategy('browse');
        } else {
          setRowsError(res.data?.error || `HTTP ${res.status}`);
        }
      }
    } catch (err) {
      setRowsError(err.message || 'Failed to load catalog');
    } finally {
      setRowsLoading(false);
    }
  }

  useEffect(() => {
    loadStatus();
    loadRows('', new Set(SEARCHABLE_KINDS));
  }, []);

  // Debounced search-on-type
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => loadRows(query, activeKinds), 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, activeKinds]);

  // Auto-refresh status while a scan is in flight.
  useEffect(() => {
    const inProgress = status?.latestRun && (status.latestRun.status === 'running' || status.latestRun.status === 'queued');
    if (!inProgress) return;
    const t = setInterval(() => loadStatus({ softFail: true }), STATUS_POLL_MS);
    return () => clearInterval(t);
  }, [status?.latestRun?.status]);

  async function triggerIndex(chosenScope) {
    setScanning(true);
    setScope(chosenScope);
    try {
      const res = await API.post(
        '/api/reports/index',
        { scope: chosenScope, scanBip: true, scanOtbi: true },
        { validateStatus: () => true }
      );
      if (res.status >= 400) {
        setStatusErr(formatStatusError(res.status, res.data?.error));
      } else {
        setStatusWarn('');
      }
    } catch (err) {
      setStatusErr(formatStatusError(err.response?.status, err.message));
    } finally {
      setScanning(false);
      loadStatus({ softFail: true });
    }
  }

  function toggleKind(kind) {
    setActiveKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  }

  function copyPath(path) {
    if (navigator?.clipboard) navigator.clipboard.writeText(String(path));
  }

  function handoffToCopilot(row) {
    if (typeof onAskCopilot !== 'function') return;
    const meta = KIND_META[row.kind];
    onAskCopilot({
      feature: 'report',
      prompt:
        `Tell me about this ${meta?.label || row.kind}: "${row.displayName}" at ` +
        `${row.path}. What is it used for, what parameters does it take, ` +
        `and how would I run it?`,
      context: { catalogId: row._id, kind: row.kind, path: row.path },
    });
  }

  const totals = status?.catalogTotals || {};
  const grandTotal = status?.grandTotal || 0;
  const latestRun = status?.latestRun || null;
  const inProgress = latestRun?.status === 'running' || latestRun?.status === 'queued';
  const hasIndex = grandTotal > 0;

  const filterChips = useMemo(() => {
    return Object.entries(KIND_META).map(([kind, meta]) => {
      const count = totals[kind] || 0;
      const active = activeKinds.has(kind);
      return { kind, meta, count, active };
    });
  }, [totals, activeKinds]);

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-semibold text-slate-800">Reports Catalog</h2>

      {/* ─── Index status panel ─── */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <Library className="h-4 w-4 text-cyan-600" />
            Index status
          </div>
          {statusLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
          {latestRun && statusPill(latestRun)}
          {latestRun?.scope && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
              {latestRun.scope}
            </span>
          )}
          {inProgress && (
            <span className="text-xs text-cyan-600">
              … walking catalog. This polls automatically.
            </span>
          )}
          <div className="ml-auto flex gap-2">
            <button
              type="button"
              onClick={() => triggerIndex('quick')}
              disabled={scanning || inProgress}
              className="flex items-center gap-1.5 rounded-md bg-cyan-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-cyan-800 disabled:bg-slate-300"
              title="Shallow walk: subject areas + top folders. Recommended on Heroku (~1–3 min, runs in background)."
            >
              {(scanning && scope === 'quick') || inProgress ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Quick scan
            </button>
            <button
              type="button"
              onClick={() => triggerIndex('full')}
              disabled={scanning || inProgress}
              className="flex items-center gap-1.5 rounded-md border border-cyan-200 bg-white px-3 py-1.5 text-xs font-medium text-cyan-700 hover:bg-cyan-50 disabled:opacity-50"
              title="Walk the entire catalog. Runs in background but can take 10–30+ min on Heroku — prefer Quick scan unless you need full depth."
            >
              {scanning && scope === 'full' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Full scan
            </button>
          </div>
        </div>

        {statusWarn && !statusErr && (
          <div className="mb-3 flex items-start gap-2 rounded-md bg-amber-50 p-2.5 text-xs text-amber-900">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {statusWarn}
          </div>
        )}

        {statusErr && (
          <div className="mb-3 flex items-start gap-2 rounded-md bg-rose-50 p-2.5 text-xs text-rose-800">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {statusErr}
          </div>
        )}

        {hasIndex ? (
          <>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
              <CountTile icon={FileSpreadsheet} label="BIP Reports" count={totals.bipReport || 0} accent="bg-emerald-100 text-emerald-700" />
              <CountTile icon={Database} label="BIP Data Models" count={totals.bipDataModel || 0} accent="bg-violet-100 text-violet-700" />
              <CountTile icon={Library} label="Subject Areas" count={totals.otbiSubjectArea || 0} accent="bg-cyan-100 text-cyan-700" />
              <CountTile icon={Sparkles} label="OTBI Analyses" count={totals.otbiAnalysis || 0} accent="bg-amber-100 text-amber-700" />
              <CountTile icon={LayoutDashboard} label="Dashboards" count={totals.otbiDashboard || 0} accent="bg-blue-100 text-blue-700" />
              <CountTile icon={Folder} label="Folders" count={(totals.bipFolder || 0) + (totals.otbiFolder || 0)} accent="bg-slate-100 text-slate-700" />
            </div>

            {latestRun && (
              <div className="mt-3 grid gap-3 text-xs text-slate-500 sm:grid-cols-2 lg:grid-cols-4">
                {latestRun.finishedAt && (
                  <div>
                    <div className="font-semibold uppercase tracking-wide text-slate-400">Last refresh</div>
                    <div className="text-slate-700">{relTime(latestRun.finishedAt)}</div>
                  </div>
                )}
                {latestRun.bipRoot && (
                  <div>
                    <div className="font-semibold uppercase tracking-wide text-slate-400">BIP root</div>
                    <div className="break-all font-mono text-slate-700">{latestRun.bipRoot}</div>
                  </div>
                )}
                {latestRun.otbiRoot && (
                  <div>
                    <div className="font-semibold uppercase tracking-wide text-slate-400">OTBI root</div>
                    <div className="break-all font-mono text-slate-700">{latestRun.otbiRoot}</div>
                  </div>
                )}
                {latestRun.summary && (
                  <div className="sm:col-span-2 lg:col-span-4">
                    <div className="font-semibold uppercase tracking-wide text-slate-400">Summary</div>
                    <div className="text-slate-700">{latestRun.summary}</div>
                  </div>
                )}
                {(latestRun.errorLogTotal || latestRun.errorLog?.length) > 0 && (
                  <details className="sm:col-span-2 lg:col-span-4">
                    <summary className="cursor-pointer text-amber-700">
                      {latestRun.errorLogTotal || latestRun.errorLog.length} non-fatal error(s) during scan
                      {latestRun.errorLogTruncated ? ' (showing latest)' : ''} — show details
                    </summary>
                    <ul className="mt-1 max-h-40 overflow-y-auto rounded bg-slate-50 p-2 text-[11px] text-slate-600">
                      {latestRun.errorLog.slice(0, 20).map((e, i) => (
                        <li key={i} className="border-b border-slate-200/70 py-1 last:border-0">
                          <span className="font-mono text-rose-700">{e.source}</span>
                          {e.path && <span> · {e.path}</span>}
                          <div className="text-slate-700">{e.message}</div>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="rounded-md bg-slate-50 p-6 text-center text-sm text-slate-500">
            Catalog is empty. Run <span className="font-medium text-slate-700">Quick scan</span> to index this tenant.
          </div>
        )}
      </div>

      {/* ─── Search + filters ─── */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
          <Search className="h-4 w-4 text-slate-500" />
          Search the catalog
        </label>
        <div className="relative">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search reports and subject areas…"
            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 pl-9 text-sm text-slate-800 focus:border-cyan-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
            disabled={!hasIndex}
          />
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          {rowsLoading && (
            <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-slate-400" />
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {filterChips.map(({ kind, meta, count, active }) => {
            const Icon = meta.icon;
            return (
              <button
                key={kind}
                type="button"
                onClick={() => toggleKind(kind)}
                className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                  active ? meta.chipClass : 'border-slate-200 bg-white text-slate-400 hover:border-slate-300'
                }`}
                title={`Toggle ${meta.label}`}
              >
                <Icon className="h-3 w-3" />
                {meta.short}
                <span className={`rounded-full px-1.5 ${active ? 'bg-white/60' : 'bg-slate-100'}`}>
                  {fmtCount(count)}
                </span>
              </button>
            );
          })}
        </div>
        <div className="mt-2 text-xs text-slate-400">
          {strategy === 'browse'
            ? 'Browsing alphabetically. Type to search.'
            : strategy === 'regex'
            ? 'Falling back to substring match.'
            : 'Ranked by relevance.'}{' '}
          Showing {rows.length} of {grandTotal.toLocaleString()} entries.
        </div>
      </div>

      {/* ─── Results table ─── */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {rowsError && (
          <div className="flex items-start gap-2 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {rowsError}
          </div>
        )}
        {rows.length === 0 && !rowsLoading && !rowsError ? (
          <div className="px-4 py-10 text-center text-sm text-slate-500">
            {hasIndex
              ? 'No catalog entries match those filters.'
              : 'Run a scan to populate the catalog first.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Type</th>
                <th className="px-3 py-2 text-left font-semibold">Name</th>
                <th className="px-3 py-2 text-left font-semibold">Path</th>
                <th className="px-3 py-2 text-left font-semibold">Modified</th>
                <th className="px-3 py-2 text-right font-semibold"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const meta = KIND_META[r.kind] || { label: r.kind, icon: FileSpreadsheet, chipClass: 'bg-slate-100', iconClass: 'text-slate-500' };
                const Icon = meta.icon;
                return (
                  <tr key={r._id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 align-top">
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${meta.chipClass}`}>
                        <Icon className="h-3 w-3" />
                        {meta.short}
                      </span>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="font-medium text-slate-800">{r.displayName}</div>
                      {r.description && (
                        <div className="mt-0.5 max-w-prose text-xs text-slate-500 line-clamp-2">
                          {r.description}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="break-all font-mono text-[11px] text-slate-500">{r.path}</div>
                    </td>
                    <td className="px-3 py-2 align-top text-xs text-slate-500">
                      {r.lastModified ? relTime(r.lastModified) : ''}
                      {r.owner && <div className="text-[10px] text-slate-400">by {r.owner}</div>}
                    </td>
                    <td className="px-3 py-2 align-top text-right">
                      <div className="flex justify-end gap-1">
                        {r.kind === 'bipReport' && (
                          <button
                            type="button"
                            onClick={() => setRunRow(r)}
                            className="flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700"
                            title="Run this report with parameters"
                          >
                            <Play className="h-3 w-3" />
                            Run
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => copyPath(r.path)}
                          className="rounded-md border border-slate-200 bg-white p-1 text-slate-500 hover:bg-slate-50"
                          title="Copy path"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                        {onAskCopilot && (
                          <button
                            type="button"
                            onClick={() => handoffToCopilot(r)}
                            className="rounded-md border border-cyan-200 bg-white p-1 text-cyan-600 hover:bg-cyan-50"
                            title="Ask Report Copilot about this"
                          >
                            <Sparkles className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {runRow && (
        <RunReportModal report={runRow} onClose={() => setRunRow(null)} />
      )}
    </div>
  );
}
